import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Home as HomeIcon, Building2, Key, Wand2, AppWindow, Clock, ShieldCheck, Leaf, Users, Calendar, CheckCircle2, Phone, ArrowRight, MapPin, Star, Plus, Minus } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BrandMark from "@/components/BrandMark";
import Reveal from "@/components/Reveal";
import FloatingSparkles from "@/components/FloatingSparkles";
import BeforeAfter from "@/components/BeforeAfter";
import { api, formatEUR } from "@/lib/api";

const SERVICE_ICONS = {
    home: HomeIcon,
    office: Building2,
    end_of_tenancy: Key,
    deep: Wand2,
    window: AppWindow,
    hourly: Clock,
    airbnb: Sparkles,
};

const SERVICE_IMAGES = {
    home: "https://images.pexels.com/photos/29012619/pexels-photo-29012619.jpeg",
    office: "https://images.pexels.com/photos/35058546/pexels-photo-35058546.jpeg",
    end_of_tenancy: "https://images.pexels.com/photos/6587852/pexels-photo-6587852.jpeg",
    deep: "https://images.pexels.com/photos/6587859/pexels-photo-6587859.jpeg",
    window: "https://images.pexels.com/photos/14633677/pexels-photo-14633677.jpeg",
    hourly: "https://images.pexels.com/photos/6195119/pexels-photo-6195119.jpeg",
    airbnb: "https://images.pexels.com/photos/6969831/pexels-photo-6969831.jpeg",
};

const FAQS = [
    { q: "Do I need to be home during the clean?", a: "Not at all. Most of our regulars leave a key or trust us with a code. If you prefer to be home, that's welcome too." },
    { q: "Do you bring your own cleaning products?", a: "Yes — every visit. We use premium eco-friendly products that are safe for children, pets and allergies. If you have preferred products you'd like us to use, we're happy to accommodate." },
    { q: "Are your cleaners professionally trained?", a: "Absolutely. Every team member is personally trained on our Signature standard before their first solo clean, and we work only with people we know and trust." },
    { q: "What happens if I'm not happy with the clean?", a: "Our 100% satisfaction guarantee kicks in — just let us know within 24 hours of the visit and we'll come back and re-clean the area free of charge." },
    { q: "How is pricing calculated?", a: "We price based on service type + property size + any extras. You get an instant quote on our booking page. The final price is confirmed after a quick walkthrough on the day." },
    { q: "How do I pay?", a: "Pay on the day the clean is complete — we accept cash, card and Revolut/bank transfer. No deposit required." },
    { q: "Do you clean at weekends?", a: "Yes — we operate Monday through Saturday. Weekend slots book up first, so booking a few days ahead is recommended." },
    { q: "Can I book a one-off or does it have to be recurring?", a: "Both. One-off cleans are perfectly welcome. If you decide to make it recurring, you unlock 5-15% off depending on frequency." },
    { q: "Do you offer end-of-tenancy cleans with deposit guarantee?", a: "Yes — our end-of-tenancy service is designed to pass landlord/agent inspections. If your landlord flags something, we return to re-clean it free of charge." },
    { q: "Which areas do you cover?", a: "All of Dublin (D1-D24) and neighbouring counties: Kildare, Wicklow, Meath, Louth and Fingal. Beyond 30km of the city centre a small travel fee may apply." },
];

export default function Home() {
    const [services, setServices] = useState([]);
    const [extras, setExtras] = useState([]);
    const [frequencies, setFrequencies] = useState([]);
    const [weekCount, setWeekCount] = useState(null);
    // Calculator state
    const [calc, setCalc] = useState({ service: "home", bedrooms: 0, bathrooms: 0, frequency: "one_off", extras: [], hours: 2 });
    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState(false);
    const [openFaq, setOpenFaq] = useState(null);

    useEffect(() => {
        api.get("/services").then((res) => {
            setServices(res.data.services);
            setExtras(res.data.extras);
            setFrequencies(res.data.frequencies);
        });
        api.get("/stats/booking-week").then((r) => setWeekCount(r.data.this_week)).catch(() => {});
    }, []);

    useEffect(() => {
        if (!services.length) return;
        let cancelled = false;
        setLoading(true);
        const t = setTimeout(() => {
            api.post("/quote", calc).then((res) => {
                if (cancelled) return;
                setQuote(res.data);
                setLoading(false);
            });
        }, 250);
        return () => { cancelled = true; clearTimeout(t); };
    }, [calc, services.length]);

    const toggleExtra = (k) => {
        setCalc((c) => ({ ...c, extras: c.extras.includes(k) ? c.extras.filter((x) => x !== k) : [...c.extras, k] }));
    };

    return (
        <div data-testid="home-page" className="min-h-screen bg-cream">
            <Navbar />

            {/* HERO */}
            <section className="relative min-h-screen flex items-center pt-24 overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src="https://images.pexels.com/photos/14633677/pexels-photo-14633677.jpeg"
                        alt="Luxury living room"
                        className="w-full h-full object-cover scale-110 animate-float"
                        style={{ animationDuration: "14s" }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-navy-deep via-navy/85 to-navy/40" />
                    <div className="absolute inset-0 bg-grain opacity-40" />
                    <FloatingSparkles count={18} />
                </div>

                <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 w-full">
                    <div className="max-w-3xl">
                        <BrandMark variant="hero" />

                        <motion.div
                            initial={{ opacity: 0, scaleX: 0 }}
                            animate={{ opacity: 1, scaleX: 1 }}
                            transition={{ duration: 1, delay: 1.8, ease: "easeOut" }}
                            style={{ transformOrigin: "left" }}
                            className="flex items-center gap-4 my-8"
                        >
                            <span className="h-px w-8 bg-gold" />
                            <p className="italic font-playfair text-xl md:text-2xl text-gold">Luxury Cleaning Redefined</p>
                            <span className="h-px w-8 bg-gold" />
                        </motion.div>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 2 }}
                            className="text-cream/90 font-outfit text-lg md:text-xl leading-relaxed max-w-xl mb-10"
                        >
                            We deliver exceptional cleaning with attention to detail, premium products, and a commitment to excellence. Experience the <span className="text-gold italic">signature</span> difference.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 2.2 }}
                            className="flex flex-col sm:flex-row gap-4"
                        >
                            <Link
                                to="/book"
                                data-testid="hero-book-now-btn"
                                className="relative bg-gold hover:bg-gold-light text-navy px-10 py-4 font-outfit font-semibold tracking-wide uppercase text-sm inline-flex items-center justify-center gap-3 transition-all group overflow-hidden hover:shadow-[0_0_30px_rgba(212,175,55,0.55)]"
                            >
                                <span className="relative z-10 flex items-center gap-3">
                                    Book Your Clean <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </span>
                                <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent group-hover:translate-x-full transition-transform duration-700" />
                            </Link>
                            <a
                                href="#pricing"
                                data-testid="hero-pricing-btn"
                                className="border border-gold text-gold hover:bg-gold hover:text-navy px-10 py-4 font-outfit font-semibold tracking-wide uppercase text-sm inline-flex items-center justify-center transition-all"
                            >
                                Get a Free Quote
                            </a>
                        </motion.div>

                        {weekCount !== null && weekCount > 0 && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.6, delay: 2.4 }}
                                data-testid="urgency-ticker"
                                className="mt-6 inline-flex items-center gap-3 bg-navy/60 border border-gold/40 px-4 py-2 backdrop-blur-sm"
                            >
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold opacity-75" />
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-gold" />
                                </span>
                                <span className="font-outfit text-xs uppercase tracking-[0.2em] text-cream">
                                    {weekCount} {weekCount === 1 ? "booking" : "bookings"} this week — slots filling fast
                                </span>
                            </motion.div>
                        )}

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 1, delay: 2.5 }}
                            className="mt-16 flex flex-wrap gap-x-10 gap-y-4"
                        >
                            {[
                                { icon: Leaf, label: "Eco-Friendly Products" },
                                { icon: ShieldCheck, label: "100% Satisfaction" },
                                { icon: Users, label: "Trusted Professionals" },
                            ].map((b, i) => (
                                <motion.div
                                    key={b.label}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.5, delay: 2.6 + i * 0.15 }}
                                    className="flex items-center gap-3 text-cream/80"
                                >
                                    <b.icon className="w-5 h-5 text-gold" />
                                    <span className="font-outfit text-sm tracking-wide">{b.label}</span>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* SERVICES */}
            <section id="services" className="py-24 md:py-32 px-6 md:px-12 bg-cream">
                <div className="max-w-7xl mx-auto">
                    <div className="mb-20 max-w-2xl">
                        <span className="uppercase tracking-[0.3em] text-xs font-bold text-gold-dark">— Our Services</span>
                        <h2 className="font-playfair text-4xl md:text-5xl lg:text-6xl text-navy mt-4 mb-6 tracking-tight">
                            Spotless standards.<br />
                            <span className="italic text-gold-dark">Exceptional results.</span>
                        </h2>
                        <p className="font-outfit text-base md:text-lg text-muted-foreground">
                            From regular home upkeep to full end-of-tenancy turnarounds — every clean is delivered to a single standard: ours.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
                        {services.map((s, idx) => {
                            const Icon = SERVICE_ICONS[s.key] || Sparkles;
                            return (
                                <Reveal key={s.key} delay={idx * 0.1}>
                                    <motion.div
                                        whileHover={{ y: -8 }}
                                        transition={{ type: "spring", stiffness: 220, damping: 20 }}
                                        data-testid={`service-card-${s.key}`}
                                        className={`group ${idx % 2 === 1 ? "lg:mt-12" : ""}`}
                                    >
                                        <div className="relative overflow-hidden border border-cream-border mb-6 aspect-[4/5]">
                                            <img
                                                src={SERVICE_IMAGES[s.key]}
                                                alt={s.name}
                                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-navy/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                            <div className="absolute top-4 left-4 w-12 h-12 bg-navy border border-gold flex items-center justify-center group-hover:rotate-12 transition-transform duration-500">
                                                <Icon className="w-5 h-5 text-gold" />
                                            </div>
                                        </div>
                                        <div className="flex items-start justify-between mb-2">
                                            <h3 className="font-playfair text-2xl text-navy">{s.name}</h3>
                                            <span className="font-outfit font-semibold text-gold-dark whitespace-nowrap ml-4">{s.fixed_label}</span>
                                        </div>
                                        <p className="font-outfit text-sm text-muted-foreground leading-relaxed">{s.description}</p>
                                        <Link
                                            to={`/book?service=${s.key}`}
                                            data-testid={`service-book-btn-${s.key}`}
                                            className="inline-flex items-center gap-2 mt-4 text-navy font-outfit text-sm font-semibold uppercase tracking-wider hover:text-gold-dark transition-colors group/btn"
                                        >
                                            Book This Service <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                                        </Link>
                                    </motion.div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* PRICING CALCULATOR */}
            <section id="pricing" className="py-24 md:py-32 px-6 md:px-12 bg-navy text-cream relative overflow-hidden">
                <div className="absolute inset-0 bg-grain opacity-30 pointer-events-none" />
                <FloatingSparkles count={14} />
                <div className="max-w-6xl mx-auto relative">
                    <Reveal>
                        <div className="text-center mb-16">
                            <span className="uppercase tracking-[0.3em] text-xs font-bold text-gold">— Instant Quote</span>
                            <h2 className="font-playfair text-4xl md:text-5xl lg:text-6xl mt-4 mb-4 tracking-tight">
                                Build Your <span className="italic text-gold">Signature</span> Quote
                            </h2>
                            <p className="font-outfit text-base md:text-lg text-cream/70 max-w-2xl mx-auto">
                                Tell us about your space and we&apos;ll give you an honest estimate — all prices in Euros, no surprises.
                            </p>
                        </div>
                    </Reveal>

                    <div className="grid lg:grid-cols-5 gap-10">
                        <div className="lg:col-span-3 space-y-8">
                            {/* Service Selector */}
                            <div>
                                <label className="uppercase tracking-[0.2em] text-xs font-bold text-gold mb-4 block">Service</label>
                                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {services.map((s) => (
                                        <button
                                            key={s.key}
                                            data-testid={`calc-service-${s.key}`}
                                            onClick={() => setCalc((c) => ({ ...c, service: s.key }))}
                                            className={`text-left p-4 border transition-all font-outfit ${
                                                calc.service === s.key
                                                    ? "border-gold bg-gold/10 text-gold"
                                                    : "border-cream/20 text-cream/80 hover:border-gold/50"
                                            }`}
                                        >
                                            <div className="text-sm font-semibold">{s.name}</div>
                                            <div className="text-xs opacity-70 mt-1">{s.fixed_label}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Sizes — bedrooms/bathrooms for standard, hours for hourly */}
                            {calc.service === "hourly" ? (
                                <div>
                                    <label className="uppercase tracking-[0.2em] text-xs font-bold text-gold mb-4 block">Hours <span className="text-cream/60 normal-case tracking-normal text-[10px]">(€30 / hr)</span></label>
                                    <div className="flex flex-wrap gap-2">
                                        {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                                            <button
                                                key={n}
                                                data-testid={`calc-hours-${n}`}
                                                onClick={() => setCalc((c) => ({ ...c, hours: n }))}
                                                className={`w-11 h-11 border font-outfit text-sm ${calc.hours === n ? "border-gold bg-gold text-navy" : "border-cream/20 text-cream/70 hover:border-gold"}`}
                                            >
                                                {n}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="grid sm:grid-cols-2 gap-6">
                                    <div>
                                        <label className="uppercase tracking-[0.2em] text-xs font-bold text-gold mb-4 block">Bedrooms</label>
                                        <div className="flex gap-2">
                                            {[0, 1, 2, 3, 4, 5].map((n) => (
                                                <button
                                                    key={n}
                                                    data-testid={`calc-bedroom-${n}`}
                                                    onClick={() => setCalc((c) => ({ ...c, bedrooms: n }))}
                                                    className={`w-10 h-10 border font-outfit text-sm transition-all ${
                                                        calc.bedrooms === n ? "border-gold bg-gold text-navy" : "border-cream/20 text-cream/70 hover:border-gold"
                                                    }`}
                                                >
                                                    {n}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="uppercase tracking-[0.2em] text-xs font-bold text-gold mb-4 block">Bathrooms</label>
                                        <div className="flex gap-2">
                                            {[0, 1, 2, 3, 4].map((n) => (
                                                <button
                                                    key={n}
                                                    data-testid={`calc-bathroom-${n}`}
                                                    onClick={() => setCalc((c) => ({ ...c, bathrooms: n }))}
                                                    className={`w-10 h-10 border font-outfit text-sm transition-all ${
                                                        calc.bathrooms === n ? "border-gold bg-gold text-navy" : "border-cream/20 text-cream/70 hover:border-gold"
                                                    }`}
                                                >
                                                    {n}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Frequency */}
                            <div>
                                <label className="uppercase tracking-[0.2em] text-xs font-bold text-gold mb-4 block">Frequency</label>
                                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
                                    {frequencies.map((f) => (
                                        <button
                                            key={f.key}
                                            data-testid={`calc-freq-${f.key}`}
                                            onClick={() => setCalc((c) => ({ ...c, frequency: f.key }))}
                                            className={`px-3 py-2 border font-outfit text-xs transition-all ${
                                                calc.frequency === f.key ? "border-gold bg-gold text-navy" : "border-cream/20 text-cream/70 hover:border-gold"
                                            }`}
                                        >
                                            {f.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Extras */}
                            <div>
                                <label className="uppercase tracking-[0.2em] text-xs font-bold text-gold mb-4 block">Extras (optional)</label>
                                <div className="flex flex-wrap gap-2">
                                    {extras.map((e) => (
                                        <button
                                            key={e.key}
                                            data-testid={`calc-extra-${e.key}`}
                                            onClick={() => toggleExtra(e.key)}
                                            className={`px-4 py-2 border font-outfit text-xs transition-all ${
                                                calc.extras.includes(e.key)
                                                    ? "border-gold bg-gold/10 text-gold"
                                                    : "border-cream/20 text-cream/70 hover:border-gold"
                                            }`}
                                        >
                                            {e.label} <span className="opacity-70 ml-1">+€{e.price}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* QUOTE PANEL */}
                        <div className="lg:col-span-2">
                            <div className="border border-gold/40 bg-navy-deep p-8 sticky top-28">
                                <div className="flex items-center gap-2 mb-2">
                                    <Sparkles className="w-4 h-4 text-gold" />
                                    <span className="uppercase tracking-[0.2em] text-xs font-bold text-gold">Your Estimate</span>
                                </div>
                                {(() => {
                                    const configured = calc.service === "hourly"
                                        ? calc.hours > 0
                                        : (calc.bedrooms > 0 || calc.bathrooms > 0 || calc.extras.length > 0);
                                    return configured ? (
                                        <>
                                            <div className="font-playfair text-5xl md:text-6xl text-cream my-4" data-testid="quote-total">
                                                {loading ? "—" : quote ? formatEUR(quote.total) : "—"}
                                            </div>
                                            <div className="gold-divider my-6" />
                                            {quote && !loading && (
                                                <ul className="space-y-2 font-outfit text-sm text-cream/80">
                                                    <li className="flex justify-between"><span>Service</span><span>{quote.service_name}</span></li>
                                                    {quote.service === "hourly" && <li className="flex justify-between"><span>Rate</span><span>€{quote.hourly_rate}/hr × {quote.hours}h</span></li>}
                                                    {quote.service !== "hourly" && <li className="flex justify-between"><span>Base</span><span>{formatEUR(quote.base_price)}</span></li>}
                                                    {quote.bedrooms_cost > 0 && <li className="flex justify-between"><span>Bedrooms</span><span>{formatEUR(quote.bedrooms_cost)}</span></li>}
                                                    {quote.bathrooms_cost > 0 && <li className="flex justify-between"><span>Bathrooms</span><span>{formatEUR(quote.bathrooms_cost)}</span></li>}
                                                    {quote.extras_total > 0 && <li className="flex justify-between"><span>Extras</span><span>{formatEUR(quote.extras_total)}</span></li>}
                                                    {quote.frequency_discount_multiplier < 1 && (
                                                        <li className="flex justify-between text-gold">
                                                            <span>Frequency discount</span>
                                                            <span>−{Math.round((1 - quote.frequency_discount_multiplier) * 100)}%</span>
                                                        </li>
                                                    )}
                                                </ul>
                                            )}
                                        </>
                                    ) : (
                                        <div data-testid="quote-empty" className="my-6 py-8 border border-dashed border-gold/30 text-center">
                                            <div className="font-playfair text-2xl text-cream/90 mb-2">Build your quote</div>
                                            <p className="font-outfit text-xs text-cream/60 leading-relaxed px-4">
                                                Pick your service and property size on the left — your estimate will appear here instantly.
                                            </p>
                                        </div>
                                    );
                                })()}
                                <Link
                                    to={`/book?service=${calc.service}&bedrooms=${calc.bedrooms}&bathrooms=${calc.bathrooms}&frequency=${calc.frequency}&hours=${calc.hours}&extras=${calc.extras.join(",")}`}
                                    data-testid="quote-proceed-btn"
                                    className="mt-8 w-full bg-gold hover:bg-gold-light text-navy py-4 font-outfit font-semibold tracking-wide uppercase text-sm flex items-center justify-center gap-3 transition-colors"
                                >
                                    Continue to Booking <ArrowRight className="w-4 h-4" />
                                </Link>
                                <p className="text-xs text-cream/50 mt-4 text-center italic">
                                    Estimate only — final price confirmed after walkthrough.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* WHY US */}
            <section id="why-us" className="py-24 md:py-32 px-6 md:px-12 bg-cream">
                <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
                    <Reveal>
                        <motion.img
                            whileHover={{ scale: 1.02 }}
                            transition={{ type: "spring", stiffness: 180, damping: 20 }}
                            src="https://images.pexels.com/photos/14633677/pexels-photo-14633677.jpeg"
                            alt="Luxury interior"
                            className="w-full aspect-[4/5] object-cover border border-cream-border"
                        />
                    </Reveal>
                    <Reveal delay={0.2}>
                        <div>
                            <span className="uppercase tracking-[0.3em] text-xs font-bold text-gold-dark">— Why Choose Us</span>
                            <h2 className="font-playfair text-4xl md:text-5xl text-navy mt-4 mb-6 tracking-tight">
                                We treat your space<br /><span className="italic text-gold-dark">like our own.</span>
                            </h2>
                            <p className="font-outfit text-muted-foreground mb-10 leading-relaxed">
                                Every Signature Shine clean is delivered by trained professionals, using premium eco-friendly products and an obsession for detail you can see — and feel — the moment you walk in.
                            </p>
                            <ul className="space-y-5">
                                {[
                                    "Reliable & Trustworthy",
                                    "Attention to Detail",
                                    "High-Quality Eco Products",
                                    "Flexible Scheduling",
                                    "100% Satisfaction Guaranteed",
                                ].map((item, i) => (
                                    <motion.li
                                        key={item}
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.5, delay: 0.3 + i * 0.08 }}
                                        className="flex items-start gap-4 group cursor-default"
                                    >
                                        <CheckCircle2 className="w-6 h-6 text-gold-dark shrink-0 mt-0.5 group-hover:rotate-12 group-hover:scale-110 transition-transform" />
                                        <span className="font-playfair text-xl text-navy">{item}</span>
                                    </motion.li>
                                ))}
                            </ul>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* BEFORE / AFTER */}
            <section id="before-after" className="py-24 md:py-32 px-6 md:px-12 bg-cream-light border-t border-cream-border">
                <div className="max-w-7xl mx-auto">
                    <Reveal>
                        <div className="text-center mb-14 max-w-2xl mx-auto">
                            <span className="uppercase tracking-[0.3em] text-xs font-bold text-gold-dark">— Before &amp; After</span>
                            <h2 className="font-playfair text-4xl md:text-5xl lg:text-6xl text-navy mt-4 tracking-tight">
                                Slide to see the <span className="italic text-gold-dark">transformation</span>
                            </h2>
                            <p className="font-outfit text-muted-foreground mt-4">
                                Drag the gold handle left or right to reveal the difference.
                            </p>
                        </div>
                    </Reveal>

                    <div className="grid md:grid-cols-2 gap-8 lg:gap-10">
                        {[
                            {
                                label: "Kitchen Deep Clean",
                                before: `${process.env.REACT_APP_BACKEND_URL}/api/uploads/before_after/kitchen_before.png`,
                                after: `${process.env.REACT_APP_BACKEND_URL}/api/uploads/before_after/kitchen_after.png`,
                            },
                            {
                                label: "Bathroom Restoration",
                                before: `${process.env.REACT_APP_BACKEND_URL}/api/uploads/before_after/bathroom_before.png`,
                                after: `${process.env.REACT_APP_BACKEND_URL}/api/uploads/before_after/bathroom_after.png`,
                            },
                            {
                                label: "Living Room Refresh",
                                before: `${process.env.REACT_APP_BACKEND_URL}/api/uploads/before_after/living_room_before.png`,
                                after: `${process.env.REACT_APP_BACKEND_URL}/api/uploads/before_after/living_room_after.png`,
                            },
                        ].map((p, i) => (
                            <Reveal key={i} delay={i * 0.1}>
                                <BeforeAfter beforeSrc={p.before} afterSrc={p.after} label={p.label} testid={`before-after-${i}`} />
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* IN ACTION — Video showcase */}
            <section id="in-action" className="py-24 md:py-32 px-6 md:px-12 bg-navy-deep text-cream relative overflow-hidden">
                <div className="absolute inset-0 bg-grain opacity-30 pointer-events-none" />
                <div className="max-w-7xl mx-auto relative">
                    <Reveal>
                        <div className="text-center mb-14 max-w-2xl mx-auto">
                            <span className="uppercase tracking-[0.3em] text-xs font-bold text-gold">— Watch Our Story</span>
                            <h2 className="font-playfair text-4xl md:text-5xl lg:text-6xl mt-4 tracking-tight">
                                Meet <span className="italic text-gold">Signature Shine Co.</span>
                            </h2>
                            <p className="font-outfit text-cream/70 mt-4">
                                A quick look at who we are and how we treat every home like our own.
                            </p>
                        </div>
                    </Reveal>

                    <Reveal delay={0.15}>
                        <div className="relative aspect-video border border-gold/30 overflow-hidden mb-12 group max-w-4xl mx-auto">
                            <video
                                data-testid="hero-video"
                                autoPlay
                                muted
                                loop
                                playsInline
                                controls
                                poster="https://images.pexels.com/photos/4239031/pexels-photo-4239031.jpeg"
                                className="w-full h-full object-cover"
                            >
                                <source src="https://customer-assets.emergentagent.com/job_euro-clean-scheduler/artifacts/mgb905mu_lv_0_20260619055511.mp4" type="video/mp4" />
                            </video>
                            <div className="absolute top-4 left-4 bg-gold text-navy text-[10px] uppercase tracking-[0.3em] px-3 py-1 font-outfit font-semibold pointer-events-none">
                                Featured Ad
                            </div>
                        </div>
                    </Reveal>

                    {/* Photo gallery */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
                        {[
                            "smiling_portrait",
                            "polishing_marble",
                            "team_two_smiling",
                            "dusting_chandelier",
                            "greeting_at_door",
                        ].map((name, i) => (
                            <Reveal key={i} delay={i * 0.07}>
                                <motion.div
                                    whileHover={{ scale: 1.04 }}
                                    transition={{ type: "spring", stiffness: 220, damping: 20 }}
                                    data-testid={`gallery-img-${i}`}
                                    className={`relative overflow-hidden border border-gold/20 ${i === 0 || i === 3 ? "aspect-[3/4]" : "aspect-square"}`}
                                >
                                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/brand/${name}.png`} alt={`Signature Shine team ${i + 1}`} className="w-full h-full object-cover hover:scale-110 transition-transform duration-1000" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/60 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-500" />
                                </motion.div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section id="testimonials" className="py-24 md:py-32 px-6 md:px-12 bg-cream-light border-t border-cream-border">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16 max-w-2xl mx-auto">
                        <span className="uppercase tracking-[0.3em] text-xs font-bold text-gold-dark">— Testimonials</span>
                        <h2 className="font-playfair text-4xl md:text-5xl text-navy mt-4 tracking-tight">
                            Loved by <span className="italic text-gold-dark">discerning</span> homes across Ireland.
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            { quote: "Came home to a house that looked better than the day we moved in. The attention to detail is on another level.", name: "Aoife M.", role: "Dublin 4" },
                            { quote: "Used Signature Shine for our end-of-tenancy clean — got our full deposit back without a single question. Worth every euro.", name: "Conor & Niamh", role: "Rathmines" },
                            { quote: "We use them weekly for our offices. Always punctual, always immaculate. The team is genuinely lovely.", name: "Rachel K.", role: "Office Manager, IFSC" },
                        ].map((t, i) => (
                            <Reveal key={i} delay={i * 0.15}>
                                <motion.figure
                                    whileHover={{ y: -6, boxShadow: "0 18px 40px -20px rgba(11,27,59,0.35)" }}
                                    transition={{ type: "spring", stiffness: 220, damping: 22 }}
                                    data-testid={`testimonial-${i}`}
                                    className="bg-white border border-cream-border p-8 relative h-full"
                                >
                                    <div className="font-playfair text-7xl text-gold leading-none absolute top-2 left-4 opacity-30">&ldquo;</div>
                                    <blockquote className="font-playfair italic text-lg text-navy leading-relaxed relative">{t.quote}</blockquote>
                                    <div className="gold-divider my-6" />
                                    <figcaption className="font-outfit">
                                        <div className="text-navy font-semibold">{t.name}</div>
                                        <div className="text-muted-foreground text-xs uppercase tracking-wider mt-1">{t.role}</div>
                                    </figcaption>
                                </motion.figure>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* SERVICE AREA */}
            <section id="service-area" className="py-24 md:py-32 px-6 md:px-12 bg-cream border-t border-cream-border">
                <div className="max-w-5xl mx-auto text-center">
                    <MapPin className="w-6 h-6 text-gold-dark mx-auto mb-3" />
                    <span className="uppercase tracking-[0.3em] text-xs font-bold text-gold-dark">— Where we clean</span>
                    <h2 className="font-playfair text-4xl md:text-5xl text-navy mt-4 tracking-tight">
                        Proudly serving <span className="italic text-gold-dark">Dublin</span> &amp; surrounding counties.
                    </h2>
                    <p className="font-outfit text-base text-muted-foreground mt-4 max-w-2xl mx-auto">
                        We cover most of the greater Dublin area and neighbouring counties. Don&apos;t see your area listed? Give us a call — we regularly travel further for regular customers.
                    </p>

                    <div className="grid md:grid-cols-2 gap-8 mt-14 text-left">
                        <div className="bg-white border border-cream-border p-8" data-testid="area-dublin">
                            <div className="uppercase tracking-[0.2em] text-[10px] font-bold text-gold-dark mb-3">Dublin postcodes</div>
                            <div className="font-playfair text-2xl text-navy mb-4">Dublin 1 — Dublin 24</div>
                            <div className="grid grid-cols-6 gap-2">
                                {["D1","D2","D3","D4","D5","D6","D7","D8","D9","D10","D11","D12","D13","D14","D15","D16","D17","D18","D20","D22","D24"].map((p) => (
                                    <span key={p} className="text-center border border-cream-border py-2 font-outfit text-xs text-navy hover:border-gold hover:text-gold-dark transition-colors">
                                        {p}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <div className="bg-white border border-cream-border p-8" data-testid="area-counties">
                            <div className="uppercase tracking-[0.2em] text-[10px] font-bold text-gold-dark mb-3">Surrounding counties</div>
                            <div className="font-playfair text-2xl text-navy mb-4">We travel to…</div>
                            <ul className="space-y-2 font-outfit text-sm text-navy">
                                {["Kildare (Naas, Maynooth, Celbridge, Newbridge)","Wicklow (Bray, Greystones, Wicklow Town)","Meath (Ashbourne, Ratoath, Dunboyne, Navan)","Louth (Drogheda, Dundalk)","Fingal (Swords, Malahide, Portmarnock)"].map((c) => (
                                    <li key={c} className="flex items-start gap-2">
                                        <MapPin className="w-3.5 h-3.5 text-gold-dark mt-1 shrink-0" /> {c}
                                    </li>
                                ))}
                            </ul>
                            <p className="mt-6 text-xs text-muted-foreground italic">Travel fee may apply beyond 30km of Dublin city centre.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* MEET THE TEAM */}
            <section id="about" className="py-24 md:py-32 px-6 md:px-12 bg-cream-light border-t border-cream-border">
                <div className="max-w-6xl mx-auto grid md:grid-cols-5 gap-12 items-center">
                    <div className="md:col-span-3">
                        <span className="uppercase tracking-[0.3em] text-xs font-bold text-gold-dark">— Meet the team</span>
                        <h2 className="font-playfair text-4xl md:text-5xl text-navy mt-4 tracking-tight">
                            A small team with <span className="italic text-gold-dark">big standards</span>.
                        </h2>
                        <p className="font-outfit text-base text-muted-foreground mt-5 leading-relaxed">
                            Signature Shine &amp; Co. is a family-run cleaning company built on one simple idea — treat every home like it&apos;s our own. Our team is personally trained on the small details that make the difference between a clean home and a <em>Signature</em> home.
                        </p>
                        <p className="font-outfit text-base text-muted-foreground mt-4 leading-relaxed">
                            We use premium eco-friendly products, always show up on time, and leave a handwritten note on your kitchen counter after every visit. If something isn&apos;t perfect, we come back and fix it — no questions asked.
                        </p>
                        <div className="grid grid-cols-2 gap-6 mt-8">
                            <div>
                                <div className="font-playfair text-3xl text-gold-dark">100%</div>
                                <div className="uppercase tracking-[0.15em] text-[10px] text-navy font-semibold mt-1">Satisfaction guarantee</div>
                            </div>
                            <div>
                                <div className="font-playfair text-3xl text-gold-dark">4.9★</div>
                                <div className="uppercase tracking-[0.15em] text-[10px] text-navy font-semibold mt-1">Google reviews</div>
                            </div>
                        </div>
                    </div>
                    <div className="md:col-span-2">
                        <img
                            src="https://images.pexels.com/photos/8082559/pexels-photo-8082559.jpeg"
                            alt="Signature Shine cleaning team"
                            className="w-full aspect-[4/5] object-cover border border-cream-border"
                            data-testid="team-photo"
                            loading="lazy"
                        />
                        <div className="mt-3 text-xs text-muted-foreground uppercase tracking-[0.15em] font-outfit text-center">Family-run · Personally trained</div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section id="faq" className="py-24 md:py-32 px-6 md:px-12 bg-cream border-t border-cream-border">
                <div className="max-w-3xl mx-auto">
                    <div className="text-center mb-14">
                        <span className="uppercase tracking-[0.3em] text-xs font-bold text-gold-dark">— Questions</span>
                        <h2 className="font-playfair text-4xl md:text-5xl text-navy mt-4 tracking-tight">
                            Everything you might <span className="italic text-gold-dark">wonder</span>.
                        </h2>
                    </div>
                    <div className="divide-y divide-cream-border border border-cream-border bg-white" data-testid="faq-list">
                        {FAQS.map((f, i) => {
                            const open = openFaq === i;
                            return (
                                <div key={i} data-testid={`faq-item-${i}`}>
                                    <button
                                        onClick={() => setOpenFaq(open ? null : i)}
                                        data-testid={`faq-toggle-${i}`}
                                        className="w-full flex items-center justify-between text-left px-6 py-5 hover:bg-cream-light transition-colors"
                                    >
                                        <span className="font-playfair text-lg text-navy pr-4">{f.q}</span>
                                        {open ? <Minus className="w-4 h-4 text-gold-dark shrink-0" /> : <Plus className="w-4 h-4 text-gold-dark shrink-0" />}
                                    </button>
                                    {open && (
                                        <div className="px-6 pb-5 font-outfit text-sm text-muted-foreground leading-relaxed" data-testid={`faq-answer-${i}`}>
                                            {f.a}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* GOOGLE REVIEWS CTA */}
            <section className="py-16 md:py-20 px-6 md:px-12 bg-navy text-cream border-t border-gold/20">
                <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
                    <div className="text-center md:text-left">
                        <div className="flex items-center justify-center md:justify-start gap-1 mb-3">
                            {[1,2,3,4,5].map((n) => (
                                <Star key={n} className="w-5 h-5 fill-gold text-gold" />
                            ))}
                            <span className="ml-3 font-outfit text-sm text-gold">4.9 / 5</span>
                        </div>
                        <h3 className="font-playfair text-2xl md:text-3xl">Read what our customers say on Google.</h3>
                        <p className="font-outfit text-cream/70 mt-2 text-sm">Every review is a real customer. Every rating is real gold.</p>
                    </div>
                    <a
                        href="https://www.google.com/search?q=signature+shine+and+co+ireland"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="google-reviews-link"
                        className="inline-flex items-center gap-2 bg-gold text-navy px-6 py-3 font-outfit font-semibold text-sm uppercase tracking-wider hover:bg-gold-light transition-colors whitespace-nowrap"
                    >
                        See our reviews <ArrowRight className="w-4 h-4" />
                    </a>
                </div>
            </section>

            {/* CTA STRIP */}
            <section className="py-20 px-6 md:px-12 bg-navy-deep text-cream relative overflow-hidden">
                <div className="absolute inset-0 bg-grain opacity-30 pointer-events-none" />
                <div className="max-w-6xl mx-auto relative grid md:grid-cols-3 gap-10 items-center">
                    <div className="md:col-span-2">
                        <span className="uppercase tracking-[0.3em] text-xs font-bold text-gold">— Book Today</span>
                        <h3 className="font-playfair text-3xl md:text-4xl lg:text-5xl mt-3 mb-4 tracking-tight">
                            One-off cleans or regular schedules. <span className="italic text-gold">We&apos;ve got you covered.</span>
                        </h3>
                        <div className="flex flex-wrap gap-x-8 gap-y-3 mt-6 text-cream/80 font-outfit">
                            <a href="tel:+353838798812" data-testid="cta-phone-1" className="flex items-center gap-3 hover:text-gold">
                                <Phone className="w-4 h-4 text-gold" /> +353 838 798 812
                            </a>
                            <a href="tel:+353899826029" data-testid="cta-phone-2" className="flex items-center gap-3 hover:text-gold">
                                <Phone className="w-4 h-4 text-gold" /> +353 899 826 029
                            </a>
                        </div>
                    </div>
                    <div className="flex md:justify-end">
                        <Link
                            to="/book"
                            data-testid="cta-book-btn"
                            className="bg-gold hover:bg-gold-light text-navy px-10 py-5 font-outfit font-semibold tracking-wide uppercase text-sm inline-flex items-center gap-3 transition-colors"
                        >
                            <Calendar className="w-5 h-5" /> Book Your Clean
                        </Link>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
